import { bindable, computedFrom, autoinject, BindingEngine } from "aurelia-framework";
import type { Subscription } from "aurelia-event-aggregator";
import { Reflection } from "../../../../helpers/reflection";
import { ConfigService } from "../../../../services/basic/config.service";
import type { DataGridColumn } from "../../../comp-data-grid/comp-data-grid.models";
import * as moment from "moment";
import { DataType, QueryFilter } from "../../../../models/queries.models";

export type DateRangeSelectorMode = 'default' | 'mobile-filter';

//Load styles
require("./comp-date-range-selector.scss");

@autoinject()
export class CompDateRangeSelector {

	private subscriptions: Subscription[] = [];

	@bindable()
	public start: Date | string = null;

	@bindable()
	public end: Date | string = null;

	@bindable()
	public column: DataGridColumn = null;

	@bindable()
	public mode: DateRangeSelectorMode = 'default';

	@bindable
	public alignRight: boolean = false;

	//filterElement html reference
    public filterElement: HTMLElement;

	//Determines whether filter should be displayed to the 'right' or 'left' side of its parent
    public filterContentPosition: 'right' | 'left' = 'right';

	private get columnDataType() {
		if (!this.column) {
			return null;
		}

		if (typeof (this.column.dataType) !== 'function') {
			return this.column.dataType;
		}

		return this.column.dataType(null);
	}

	@computedFrom(Reflection.getPropertyName<CompDateRangeSelector>(drs => drs.column)
		, Reflection.getPropertyPath<CompDateRangeSelector>(drs => drs.column.dataType))
	public get format(): string {
		switch (this.columnDataType) {
			case DataType.Date:
				return this.configService.config.common.defaultDateFormat;
			case DataType.DateTime:
				return this.configService.config.common.defaultDateTimeFormat;
			default:
				return this.configService.config.common.defaultDateFormat;
		}
	}

	public constructor(private element: Element, private bindingEngine: BindingEngine, private configService: ConfigService) {

	}

	attached() {
        //Initially re-position filter content based on window width
        this.calculatePosition();
        window.addEventListener('resize', this.rePositionContent);
    }

	// detached() {
    //     window.removeEventListener('resize', this.rePositionContent);
    // }

	protected bind() {
		if (this.mode === 'mobile-filter') {
			const start = this.column ? this.column.filters.filter(qf => qf.filterOperator.translatedSymbol === 'ge')[0] : null;
			const end = this.column ? this.column.filters.filter(qf => qf.filterOperator.translatedSymbol === 'le')[0] : null;

			this.start = start ? start.filterValue : this.start;
			this.end = end ? end.filterValue : this.end;

			const apply = () => {
				this.apply(false);
			};

			const startSubscription = this.bindingEngine.propertyObserver(this, Reflection.getPropertyName<CompDateRangeSelector>(drs => drs.start))
				.subscribe(apply);

			const endSubscription = this.bindingEngine.propertyObserver(this, Reflection.getPropertyName<CompDateRangeSelector>(drs => drs.end))
				.subscribe(apply);

			this.subscriptions.push(startSubscription);
			this.subscriptions.push(endSubscription);
		}

		if (this.column) {
			const property = Reflection.getPropertyName<DataGridColumn>(dgc => dgc.filters);

			const subscription = this.bindingEngine.propertyObserver(this.column, property).subscribe(() => {
				const filters: QueryFilter[] = this.column[property];

				if (filters.length > 0) {
					return;
				}

				this.start = null;
				this.end = null;
			});

			this.subscriptions.push(subscription);
		}
	}

	protected unbind() {
		if (this.subscriptions) {
			this.subscriptions.forEach(s => s.dispose());
		}
	}

	private filter() {
		if(!this.column) return;

		const filters: QueryFilter[] = [];
		const dataType = this.columnDataType;

		if (this.start) {
			const filter = QueryFilter.getQuery(this.column.name, this.start, "ge", dataType, true, false);
			filters.push(filter);
		}

		if (this.end) {
			const filter = QueryFilter.getQuery(this.column.name, this.end, "le", dataType, true, false);
			filters.push(filter);
		}

		this.column.filterActive = filters.length > 0;
		this.column.isAppliedMobile = filters.length > 0;

		if (filters.length > 0) {
			const format = this.format;

			const start = moment(this.start);
			const end = moment(this.end);

			const startDesc = start && start.isValid() ? start.format(format) : 'Beginning of Time';

			const endDesc = end && end.isValid() ? end.format(format) : 'Now';

			this.column.mobileDescription = `${startDesc} - ${endDesc}`;
		} else {
			this.column.mobileDescription = null;
		}

		this.column.filters = filters;
	}

	private dispatch(closeFilter: boolean = true): void {
		const event = new CustomEvent('apply-filters', {
			bubbles: true,
			detail: {
				from: this.start, 
				to: this.end,
				closeFilter: closeFilter,
				filters: null,
			},
		});

		this.element.dispatchEvent(event);
	}

	public apply(dispatch: boolean = true) {
		this.filter();

		if (dispatch) {
			this.dispatch();
		}
		//return value for mobileFilterClose
		return true;
	}

	public clear(): void {
		this.start = null;
		this.end = null;

		this.filter();
		this.dispatch(false);
	}

	/**
    * Reposition the content that is displayed when the filter is opened
    */
    private rePositionContent = () => {
		setTimeout(() => {
			this.calculatePosition();
		}, 100);
	}

	calculatePosition() {
        if(!this.filterElement) return;

        //Get filter 'right' position relative to the window
        const filterElementPosX = window.innerWidth - this.filterElement.getBoundingClientRect().right;

        //If the filter is currently aligned to the right hand side
        if (this.filterContentPosition == 'right') {
            //If filter is close to the window's right side, display the content to the left, else right
            this.filterContentPosition = filterElementPosX < 50 ? 'left' : 'right';
        }
        //If the filter is currently aligned to the left hand side
        else {
            //If filter is close to the window's right side, display the content to the left, else right
            this.filterContentPosition = filterElementPosX < 400 ? 'left' : 'right';
        }
    }
}
